import { useEffect, useRef } from "react";
import { useLocation } from "@tanstack/react-router";

const SESSION_KEY = "hx_session_id";

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

export function PageViewTracker() {
  const location = useLocation();
  const lastRef = useRef<{ id: number | null; path: string; startedAt: number }>({
    id: null,
    path: "",
    startedAt: Date.now(),
  });

  useEffect(() => {
    const path = location.pathname + (location.search ? location.search : "");
    if (path === lastRef.current.path) return;

    let cancelled = false;

    const flushPrevious = async () => {
      const prev = lastRef.current;
      if (prev.id != null) {
        const duration = Date.now() - prev.startedAt;
        try {
          const { updatePageViewDuration } = await import("@/server/page-views.functions");
          await updatePageViewDuration({ data: { id: prev.id, durationMs: duration } });
        } catch {}
      }
    };

    const record = async () => {
      try {
        const { recordPageView } = await import("@/server/page-views.functions");
        const id = await recordPageView({
          data: {
            sessionId: getSessionId(),
            path,
            referrer: document.referrer || null,
            userAgent: navigator.userAgent,
          },
        });
        if (cancelled) return;
        if (id != null) {
          lastRef.current = { id, path, startedAt: Date.now() };
        }
      } catch {}
    };

    flushPrevious();
    record();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search]);

  // (The final-page dwell-time flush on unload was dropped with the Supabase
  // REST PATCH; per-navigation durations are still recorded via flushPrevious.)

  return null;
}
